import { clientLogger } from "@/lib/logger";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import Link from "next/link";
import React from "react";

const Dropdown = ({
	searchValue,
	groupedData, // { car: {…}, mobile: {…} }
	searchData,
	setIsOpen,
	type,
	setSelectedModel,
	setShowDropdown,
	setSearchValue,
	assetListId,
	assetItemId,
}: DropdownProps) => {
	const handleDrawerClose = () => setIsOpen(false);
	const handleModelChange = (value: {
		manufacturer: string;
		model: string;
	}) => {
		setSelectedModel(value);
		setSearchValue(`${value?.manufacturer} ${value?.model}`);
		setShowDropdown(false);
	};

	const renderModels = (
		manufacturer: string,
		models: string[],
		productType: "car" | "mobile",
	) =>
		models.map((model, i) =>
			type === "linked" ? (
				<Link
					id={assetItemId}
					key={model}
					href={`/product/${manufacturer};${model}`}
					className={cn(
						"py-4 text-automint-onboarding-subheading text-automint-text border-b border-gray-100/60",
						i === 0 && "pt-0",
						i === models.length - 1 && "border-b-0 pb-0",
					)}
					onClick={() => {
						handleDrawerClose();
						clientLogger.info("Product selected", {
							component: "ProductCardMobile",
							productType: productType,
							productName: `${manufacturer} ${model}`,
						});
					}}
				>
					{manufacturer} {model}
					<ChevronRight className="float-right" />
				</Link>
			) : (
				<p
					id={assetItemId}
					key={model}
					className={cn(
						"pl-10 py-4 text-automint-onboarding-subheading text-automint-text border-b border-gray-100/60",
						i === models.length - 1 && "border-b-0",
					)}
					onClick={() => {
						handleModelChange({ manufacturer, model });
						clientLogger.info("Product selected", {
							component: "ProductCardMobile",
							productType: productType,
							productName: `${manufacturer} ${model}`,
						});
					}}
				>
					{manufacturer} {model}
				</p>
			),
		);

	const renderSection = (label: string, bucket: Record<string, string[]>) =>
		Object.keys(bucket).length > 0 && (
			<div
				id={assetListId}
				className="mb-6 rounded-[12px] border border-gray-200/60 bg-white/80 p-4 shadow-xs"
			>
				<h3 className="text-automint-onboarding-heading text-automint-text mb-2 font-normal">
					{label}
				</h3>
				{Object.keys(bucket).map((manufacturer) => (
					<div key={manufacturer} className="">
						<h4 className="p-2 text-automint-onboarding-heading-2 text-automint-text font-normal">
							{manufacturer}
						</h4>
						<ul className="flex flex-col rounded-xl border border-gray-100 bg-white p-4">
							{renderModels(
								manufacturer,
								bucket[manufacturer],
								label === "Cars" ? "car" : "mobile",
							)}
						</ul>
					</div>
				))}
			</div>
		);

	/** ---------- main JSX ---------- */
	if (!searchValue) return null;

	const empty =
		!Object.keys(groupedData.car).length &&
		!Object.keys(groupedData.mobile).length;

	return empty ? (
		<div className="w-full px-6 py-10 text-center text-sm text-automint-text/70">
			<p className="text-base text-automint-text/80">
				Nothing pops up for that search.
			</p>
			<p>Try a different keyword or tweak the spelling.</p>
		</div>
	) : (
		<div className="relative top-1 w-full p-2 scroll-mt-0">
			{searchData?.length > 0 &&
			(searchData?.[0]?.type).toLowerCase() === "car" ? (
				<>
					{renderSection("Cars", groupedData.car)}
					{renderSection("Devices", groupedData.mobile)}
				</>
			) : (
				<>
					{renderSection("Devices", groupedData.mobile)}
					{renderSection("Cars", groupedData.car)}
				</>
			)}
			{/* cars section */}
			{/* mobile devices section */}
		</div>
	);
};

export default Dropdown;
